import { useState, useEffect, useMemo } from 'react';
import { Header, Button, Label } from 'semantic-ui-react';
import ReactGA from 'react-ga4';

import { DealStates } from '~/constants';
import { usePortfolioContext } from '~/hooks';
import PortfolioBidTable from './developer/PortfolioBidTable';
import PortfolioBidCards from './developer/PortfolioBidCards';
import AdminControlPanel from '../admin/AdminControlPanel';
import { usePermissions } from '~/requests/permissions/usePermissions';
import RfiTable from '~/features/requestForInformation/admin/RfiTable';
import RfiQuestionnaire from '~/features/requestForInformation/investor/RfiQuestionnaire';
import InvestorBidding from './investor/InvestorBidding';
import DealExclusionCard from '~/components/DealExclusionCard';
import BuyerProfileMatchPanel from '~/features/buyerProfile/BuyerProfileMatchPanel';
import './styles.scss';

export default function PorfolioBidding() {
  const { portfolio, portfolioState, portfolioDisplayValues } = usePortfolioContext();
  const [showBidSummary, setShowBidSummary] = useState<boolean>(false);
  const [investorCount, setInvestorCount] = useState<number>(0);
  const [bidCount, setBidCount] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    const savedViewMode = localStorage.getItem('portfolioBiddingViewMode');
    if (portfolio?.projects[0].pricingType === 'OFFTAKE') {
      return 'cards';
    }
    return (savedViewMode as 'table' | 'cards') || 'cards';
  });
  const [budgetDisplayMode, setBudgetDisplayMode] = useState<'unit' | 'total'>(() => {
    const saved = localStorage.getItem('portfolioBudgetDisplayMode');
    if (portfolio?.projects[0].pricingType === 'OFFTAKE') {
      return 'unit';
    }
    return (saved as 'unit' | 'total') || 'unit';
  });
  const { permissions } = usePermissions();

  const getFilteredInvestorData = useMemo(() => {
    const { dealSummary } = portfolioDisplayValues || {};
    const { investorProjectMap } = dealSummary || {};
    const investorArray = investorProjectMap ? Object.values(investorProjectMap) : [];

    return investorArray.filter(
      (investor: any) => investor.projects && Object.keys(investor.projects).length > 0
    );
  }, [portfolioDisplayValues]);

  const [showDeclined, setShowDeclined] = useState<boolean>(() => {
    return localStorage.getItem('portfolioBiddingShowDeclined') !== 'false';
  });

  const isInvestorDeclined = (investor: any) => {
    const projects = investor?.projects ? Object.values(investor.projects) : [];
    if (!projects.length) return false;
    return projects.every((p: any) => p?.status === 'DECLINED_TO_BID' || p?.status === 'BID_REJECTED');
  };

  const declinedCount = useMemo(
    () => (getFilteredInvestorData as any[]).filter(isInvestorDeclined).length,
    [getFilteredInvestorData]
  );

  const visibleInvestorData = useMemo(
    () => showDeclined
      ? getFilteredInvestorData
      : (getFilteredInvestorData as any[]).filter((inv) => !isInvestorDeclined(inv)),
    [getFilteredInvestorData, showDeclined]
  );

  const handleShowDeclinedToggle = (show: boolean) => {
    setShowDeclined(show);
    localStorage.setItem('portfolioBiddingShowDeclined', String(show));
  };
  const bidDeadlineData = (portfolio?.bidDeadline || portfolio?.portfolio?.bidDeadline) as string;

  ReactGA.send({
    hitType: 'pageview',
    page: '/portfolio/id/bidding',
    title: `${portfolio?.name} | Conductor Solar`
  });
  document.title = `${portfolio?.name} | Conductor Solar`;

  useEffect(() => {
    if (portfolioDisplayValues && portfolioDisplayValues.dealSummary.investors.length > 0) {
      setShowBidSummary(true);
    } else {
      setShowBidSummary(false);
    }
  }, [portfolioDisplayValues]);

  useEffect(() => {
    if (portfolioDisplayValues) {
      let totalBids = 0;
      let totalInvestors = 0;
      let investorProjectBidMap = portfolioDisplayValues.dealSummary.investorProjectBidMap;
      for (const key in investorProjectBidMap) {
        for (const key2 of Object.keys(investorProjectBidMap[key])) {
          const bid = investorProjectBidMap[key][key2];
          if (bid.investorBidAmount != null) {
            totalBids += 1;
            break;
          }
        }
      };

      portfolioDisplayValues.dealSummary.projectBids[0]?.bids.forEach((bid: any) => {
        if (bid.investorBidAmount != null || bid.bidState === 'AWAITING_BID') {
          totalInvestors += 1;
        }
      });

      setBidCount(totalBids);
      setInvestorCount(totalInvestors);
    }
  }, [portfolio]);

  const handleViewModeToggle = (mode: 'table' | 'cards') => {
    setViewMode(mode);
    localStorage.setItem('portfolioBiddingViewMode', mode);
  };

  const handleBudgetDisplayModeToggle = (mode: 'unit' | 'total') => {
    setBudgetDisplayMode(mode);
    localStorage.setItem('portfolioBudgetDisplayMode', mode);
  };

  const isOperatingProject = portfolio?.projects[0]?.pricingType === 'ACQUISITION' && 
    portfolio?.projects[0]?.developmentStage === 'OPERATING';

  return (
    <div className="portfolio-bidding-tab">

      {(permissions.isAdmin || permissions.isDeveloper) && (

        <>
          {showBidSummary && (
            <div className="card">

              <div className="deal-summary-header">
                <Header as="h2">
                  Bidding on {isOperatingProject ? 'Project Acquisition' : portfolioDisplayValues?.summary?.pricingType}
                  {bidDeadlineData && (
                    <Label color='olive'>
                      Bidding
                    </Label>
                  )}
                </Header>
                {investorCount > 0 && (permissions.isAdmin || (permissions.isDeveloper && portfolio.projects[0].pricingType !== 'OFFTAKE')) ? (
                  <div>
                    <p>
                      You have {bidCount} bid{bidCount !== 1 ? 's' : ''} from {investorCount} investor{investorCount !== 1 ? 's' : ''} invited to your portfolio.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div className="view-mode-toggle" style={{ paddingBottom: '0', paddingTop: '12px' }}>
                        <span>View mode: </span>
                        <Button.Group>
                          <Button
                            size="small"
                            basic
                            active={viewMode === 'cards'}
                            onClick={() => handleViewModeToggle('cards')}
                          >
                            Cards
                          </Button>
                          <Button
                            size="small"
                            basic
                            active={viewMode === 'table'}
                            onClick={() => handleViewModeToggle('table')}
                          >
                            Table
                          </Button>
                        </Button.Group>
                      </div>

                      {viewMode === 'cards' && portfolio.projects[0].pricingType !== 'OFFTAKE' ? (
                        <div className="view-mode-toggle" style={{ paddingBottom: '0', paddingTop: '12px' }}>
                          <span>Budget display: </span>
                          <Button.Group>
                            <Button
                              size="small"
                              basic
                              active={budgetDisplayMode === 'unit'}
                              onClick={() => handleBudgetDisplayModeToggle('unit')}
                            >
                              Unit
                            </Button>
                            <Button
                              size="small"
                              basic
                              active={budgetDisplayMode === 'total'}
                              onClick={() => handleBudgetDisplayModeToggle('total')}
                            >
                              Total
                            </Button>
                          </Button.Group>
                        </div>

                      ) : null}

                      {declinedCount > 0 && (
                        <div className="view-mode-toggle" style={{ paddingBottom: '0', paddingTop: '12px' }}>
                          <span>Investor list: </span>
                          <Button.Group>
                            <Button
                              size="small"
                              basic
                              active={!showDeclined}
                              onClick={() => handleShowDeclinedToggle(false)}
                            >
                              Active
                            </Button>
                            <Button
                              size="small"
                              basic
                              active={showDeclined}
                              onClick={() => handleShowDeclinedToggle(true)}
                            >
                              All
                            </Button>
                          </Button.Group>
                        </div>
                      )}
                    </div>
                  </div>

                ) : null}
              </div>

              {viewMode === 'table' ? (
                <PortfolioBidTable showDeclined={showDeclined} />
              ) : null}

              {/* Cards view */}
              {viewMode === 'cards' && (
                <PortfolioBidCards
                  sortedBidData={visibleInvestorData}
                  budgetDisplayMode={budgetDisplayMode}
                />
              )}
            </div>
          )}

          {!showBidSummary && (
            <div className="card">
              <div className="deal-summary-empty-state">
                <Header as="h2"><i className="hourglass half icon"></i>Matching with investors...</Header>
                <Header as="h4">You will receive an email notification once investors have been invited to bid.</Header>
                <p>
                  Here&rsquo;s what&rsquo;s happening:
                  <ol>
                    <li>Your project is being reviewed for completeness, to ensure it will attract optimal bids.</li>
                    <li>
                      Conductor uses the project information that you&rsquo;ve entered to identify the best investors. We evaluate your project&rsquo;s alignment with over 100 investors across market fit, deal fit, project fit, partner fit, and recent performance. This surfaces the investors most likely to bid aggressively and follow-through on their deal terms.
                    </li>
                    <li>
                      When Conductor matches your project with an investor and invites them to bid, you&rsquo;ll receive an email notification.
                    </li>
                  </ol>
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {permissions.isInvestor && (
        <div className="card">
          <InvestorBidding />
        </div>
      )}
      {permissions?.isInvestor && (
        <RfiQuestionnaire />
      )}

      {permissions.isAdmin && <AdminControlPanel />}
      {permissions?.isAdmin && <RfiTable />}
      {permissions?.isAdmin && (
        <DealExclusionCard portfolioId={portfolio?.id} />
      )}
      {permissions?.isAdmin && (
        <BuyerProfileMatchPanel portfolioId={portfolio?.id} />
      )}

      {!permissions?.isAdmin && (
        portfolioState?.earliestState < DealStates.IN_REVIEW ||
        portfolioState?.earliestState >= DealStates.IN_DILIGENCE
      ) && (
          <>
            {window.location.assign(`/portfolio/${portfolio.id}/review`)}
          </>
        )}

    </div>
  );
}